import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import { authResendCode } from '@api/auth';
import LoginLayout from '@components/1-atoms/Layouts/LoginLayout/LoginLayout';
import PageHeader, {
  PAGE_HEADER_VARIANT_LARGE,
} from '@components/2-molecules/PageHeader';
import Button, { BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import {
  ErrorBox,
  SuccessBox,
} from '../auth.styled';
import {
  Header,
  Logo,
  BrandName,
  LoginForm,
  LoginStepContent,
} from '../Login/Login.styled';
import { CodeInputRow, DigitBox, ResendRow, ResendLink } from './VerifyEmail.styled';

const CODE_LENGTH = 6;

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { verifyEmail } = useAuth();

  // Resolve email and code-resent flag from URL params or router state
  const emailFromUrl = searchParams.get('email');
  const emailFromState = (location.state as { email?: string })?.email;
  const email = emailFromUrl || emailFromState || '';
  const codeResent = (location.state as { codeResent?: boolean })?.codeResent;
  const codeFromUrl = searchParams.get('code');

  // Initialize to 'verifying' when auto-verifying from an email link
  const [status, setStatus] = useState<'input' | 'verifying' | 'success' | 'error'>(
    codeFromUrl && emailFromUrl ? 'verifying' : 'input',
  );
  const [errorMsg, setErrorMsg] = useState('');
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // If URL already has code + email, auto-verify (link from email)
  useEffect(() => {
    if (codeFromUrl && emailFromUrl) {
      verifyEmail(emailFromUrl, codeFromUrl)
        .then(() => {
          setStatus('success');
          setTimeout(() => navigate('/auth/login', { state: { verified: true } }), 2000);
        })
        .catch((err) => {
          setStatus('error');
          setErrorMsg(err instanceof Error ? err.message : intl.formatMessage({ id: 'verifyEmail.error.verificationFailed', defaultMessage: 'Verification failed' }));
        });
    }
  }, [codeFromUrl, emailFromUrl, verifyEmail, navigate, intl]);

  // Focus first digit on mount
  useEffect(() => {
    if (!codeFromUrl) {
      inputRefs.current[0]?.focus();
    }
  }, [codeFromUrl]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      // Only allow digits
      const digit = value.replace(/\D/g, '').slice(-1);
      setDigits((prev) => {
        const next = [...prev];
        next[index] = digit;
        return next;
      });
      // Move focus forward
      if (digit && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    []
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
      if (!pasted) return;
      const newDigits = Array(CODE_LENGTH).fill('');
      for (let i = 0; i < pasted.length; i++) {
        newDigits[i] = pasted[i];
      }
      setDigits(newDigits);
      // Focus last filled or the next empty
      const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
      inputRefs.current[focusIdx]?.focus();
    },
    []
  );

  const fullCode = digits.join('');
  const isCodeComplete = fullCode.length === CODE_LENGTH;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isCodeComplete || !email) return;
      setStatus('verifying');
      setErrorMsg('');
      try {
        await verifyEmail(email, fullCode);
        setStatus('success');
        setTimeout(() => navigate('/auth/login', { state: { verified: true } }), 2000);
      } catch (err) {
        setStatus('error');
        setErrorMsg(err instanceof Error ? err.message : intl.formatMessage({ id: 'verifyEmail.error.verificationFailed', defaultMessage: 'Verification failed' }));
      }
    },
    [isCodeComplete, email, fullCode, verifyEmail, navigate, intl]
  );

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || !email) return;
    try {
      await authResendCode(email);
      setResendCooldown(60);
      setErrorMsg('');
      if (status === 'error') setStatus('input');
    } catch {
      setErrorMsg(intl.formatMessage({ id: 'verifyEmail.error.resendFailed', defaultMessage: 'Failed to resend code. Please try again.' }));
    }
  }, [resendCooldown, email, status, intl]);

  // Success state
  if (status === 'success') {
    return (
      <ThemeProvider theme={darkTheme}>
        <LoginLayout>
          <Header>
            <Logo />
            <BrandName>Aibuild OS</BrandName>
          </Header>
          <LoginForm as="div">
            <LoginStepContent>
              <PageHeader
                variant={PAGE_HEADER_VARIANT_LARGE}
                title={intl.formatMessage({ id: 'verifyEmail.verified.title', defaultMessage: 'Email verified!' })}
                subtitle={intl.formatMessage({ id: 'verifyEmail.verified.subtitle', defaultMessage: 'Your email has been verified. Redirecting to login...' })}
              />
            </LoginStepContent>
          </LoginForm>
        </LoginLayout>
      </ThemeProvider>
    );
  }

  // Auto-verifying from URL (link flow)
  if (status === 'verifying' && codeFromUrl) {
    return (
      <ThemeProvider theme={darkTheme}>
        <LoginLayout>
          <Header>
            <Logo />
            <BrandName>Aibuild OS</BrandName>
          </Header>
          <LoginForm as="div">
            <LoginStepContent>
              <PageHeader
                variant={PAGE_HEADER_VARIANT_LARGE}
                title={intl.formatMessage({ id: 'verifyEmail.verifying.title', defaultMessage: 'Verifying...' })}
                subtitle={intl.formatMessage({ id: 'verifyEmail.verifying.subtitle', defaultMessage: 'Confirming your email address...' })}
              />
            </LoginStepContent>
          </LoginForm>
        </LoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <LoginLayout>
        <Header>
          <Logo />
          <BrandName>Aibuild OS</BrandName>
        </Header>

        <LoginForm as="form" onSubmit={handleSubmit}>
          <LoginStepContent>
            <PageHeader
              variant={PAGE_HEADER_VARIANT_LARGE}
              title={intl.formatMessage({ id: 'verifyEmail.title', defaultMessage: 'Enter verification code' })}
              subtitle={intl.formatMessage({ id: 'verifyEmail.subtitle', defaultMessage: 'We sent a 6-digit code to {email}. Enter it below to verify your account.' }, { email })}
            />

            {codeResent && (
              <SuccessBox>
                {intl.formatMessage({ id: 'verifyEmail.codeResent', defaultMessage: "We've re-sent a new verification code to your email." })}
              </SuccessBox>
            )}

            <CodeInputRow onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <DigitBox
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  autoComplete="one-time-code"
                />
              ))}
            </CodeInputRow>

            {status === 'error' && (
              <ErrorBox>{errorMsg || intl.formatMessage({ id: 'verifyEmail.error.fallback', defaultMessage: 'Verification failed. Please try again.' })}</ErrorBox>
            )}

            <Button
              type="submit"
              disabled={!isCodeComplete || status === 'verifying'}
              loading={status === 'verifying'}
            >
              {status === 'verifying'
                ? intl.formatMessage({ id: 'verifyEmail.button.verifying', defaultMessage: 'Verifying...' })
                : intl.formatMessage({ id: 'verifyEmail.button.verify', defaultMessage: 'Verify Email' })}
            </Button>

            <ResendRow>
              {intl.formatMessage({ id: 'verifyEmail.resend.prompt', defaultMessage: "Didn't receive the code?" })}{' '}
              <ResendLink onClick={handleResend} disabled={resendCooldown > 0}>
                {resendCooldown > 0
                  ? intl.formatMessage({ id: 'verifyEmail.resend.countdown', defaultMessage: 'Resend in {seconds}s' }, { seconds: resendCooldown })
                  : intl.formatMessage({ id: 'verifyEmail.resend.action', defaultMessage: 'Resend code' })}
              </ResendLink>
            </ResendRow>

            <Button
              type="button"
              variant={BUTTON_VARIANT_TEXT}
              onClick={() => navigate('/auth/login')}
            >
              {intl.formatMessage({ id: 'verifyEmail.button.backToLogin', defaultMessage: 'Back to login' })}
            </Button>
          </LoginStepContent>
        </LoginForm>
      </LoginLayout>
    </ThemeProvider>
  );
};

export default VerifyEmail;
